import { CanvasEx } from '@/typings/canvas';

export interface CancelablePromise<T> extends Promise<T> {
    cancel: () => void;
}

const makeCancelablePromise = <T>(promise: Promise<T>): CancelablePromise<T> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rejectFn: (reason?: any) => void;

    const wrappedPromise = new Promise((resolve, reject) => {
        rejectFn = reject;

        Promise.resolve(promise).then(resolve).catch(reject);
    }) as CancelablePromise<T>;

    wrappedPromise.cancel = () => {
        rejectFn({
            canceled: true
        });
    };

    return wrappedPromise;
};

export const waitForDomElement = (selector: string) => {
    const cancellablePromise = makeCancelablePromise(
        new Promise((resolve, reject) => {
            const INTERVAL_MS = 50;
            const TIMEOUT_MS = 60000;
            let elapsed = 0;

            const interval = setInterval(() => {
                const element = document.querySelector(selector);
                if (element) {
                    clearInterval(interval);
                    resolve(element);
                }

                elapsed += INTERVAL_MS;
                if (elapsed > TIMEOUT_MS) {
                    clearInterval(interval);
                    reject(new Error(`Timeout after ${TIMEOUT_MS} ms`));
                }
            }, INTERVAL_MS);
        })
    );

    return cancellablePromise;
};

export const calculateCanvasDimensions = (maxWidth: number, maxHeight: number, canvas: CanvasEx) => {
    const result = {
        width: 0,
        height: 0
    };

    // By default the customily.js canvas is made to fit the container width, but in this case we want to fit
    //  a maximum width and maximum height. The -80 is to account for the height of image controls when they are shown.
    const aspectRatio = canvas._artworkWidth / canvas._artworkHeight;
    let targetWidth = maxHeight * aspectRatio;

    // If the width goes over the maximum width, limit it
    if (maxWidth > 0 && targetWidth > maxWidth) {
        targetWidth = maxWidth;
    }

    if (targetWidth > 0) {
        result.width = targetWidth;
        result.height = targetWidth / aspectRatio;
    }

    return result;
};
